import React, { useEffect, useState, useRef, useMemo } from "react";
import { withRouter } from "react-router-dom";
import { matchRoutes } from "react-router-config";
import { connect } from "react-redux";

import { asideRepositoryNavigationByModule } from "../Repository/navigation";

import styles from "./App.module.sass";

import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    downloadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    setDataFileStatusAction,
    deleteFilePermanentlyAction,
    deleteInvalidFilePermanentlyAction,
    hideFileAction,
    downloadFileAction,
    reprocessRepositoryAction,
    genericSearchAction,
    getFindingDetails,
    reprocessFileAction,
    getDownloadTemplateData,
} from "../../store/actions";

import proxy from "../../routes/proxy";

import { changeDeepObject } from "../../utils";
import { redirectNotValid, buildStaticLinks, applyThemeWithPriority, resolveDualModulePrefix } from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import { alias } from "../../utils/queryUtils";
import useUpdateThemes from "../../hooks/useUpdateThemes";

import {
    appRedirectValidations,
    controllerQueries,
    generateChildren,
    getQueryResultsArray,
    onClickConfiguratorApp,
    isPageLoading,
    GlobalPageLayout,
    useControllerInitialization,
    organizationAndProjectSelection,
    buildGenericMenu,
    buildDisplayComponents,
    buildHeaderComponents,
    headerTitle,
    getDownloadableQueries,
    generateNavigation,
    buildNavProps,
} from "../utils/utils";

const StructuredData = (props) => {
    const isInitialMount = useRef(true);
    const {
        pushModal,
        modules,
        fetch,
        match,
        route,
        location,
        history,
        reload,
        matchParams,
        projectId,
        modulePrefix,
        locale,
        fetchResults,
        fetchStatus,
        appContext,
        auth,
        userInformation,
        i18n,
    } = useControllerInitialization(props);

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const urlViewMode = searchParams.get("viewMode");
    const storedViewMode = !urlViewMode
        ? sessionStorage.getItem(`viewMode_${projectId}`)
        : null;
    const initialViewMode = urlViewMode || storedViewMode || "source";

    const [viewMode, setViewMode] = useState(initialViewMode);
    const previousViewMode = useRef(viewMode);

    const effectiveModulePrefix = resolveDualModulePrefix(modulePrefix, viewMode);
    const effectiveMatchParams = useMemo(
        () => (effectiveModulePrefix && effectiveModulePrefix !== matchParams.modulePrefix
            ? { ...matchParams, modulePrefix: effectiveModulePrefix }
            : matchParams),
        [matchParams, effectiveModulePrefix],
    );

    let { fiscalYear, importId, section, fileType } = effectiveMatchParams;
    useEffect(() => {
        if (urlViewMode && projectId) {
            sessionStorage.setItem(`viewMode_${projectId}`, urlViewMode);
        }
    }, [urlViewMode, projectId]);

    const viewModeHistory = useMemo(() => ({
        ...history,
        push: (path, state) => {
            if (urlViewMode && typeof path === "string" && !path.includes("viewMode=")) {
                const sep = path.includes("?") ? "&" : "?";
                history.push(`${path}${sep}viewMode=${urlViewMode}`, state);
            } else {
                history.push(path, state);
            }
        },
    }), [history, urlViewMode]);

    const previousFiscalYear = useRef(fiscalYear);
    const previousImportId = useRef(importId);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            previousViewMode.current = viewMode;

            if (viewMode && !searchParams.get("viewMode")) {
                const currentSearchParams = new URLSearchParams(location.search);
                currentSearchParams.set("viewMode", viewMode);
                const newSearch = currentSearchParams.toString();
                const newUrl = `${location.pathname}?${newSearch}`;
                history.replace(newUrl);
            }
            return;
        }

        if (
            viewMode &&
            previousViewMode.current !== viewMode &&
            history &&
            location
        ) {
            previousViewMode.current = viewMode;

            const currentSearchParams = new URLSearchParams(location.search);
            currentSearchParams.set("viewMode", viewMode);
            const newSearch = currentSearchParams.toString();
            const newUrl = `${location.pathname}?${newSearch}`;

            history.replace(newUrl);
        }
    }, [viewMode, location, history, searchParams]);

    useEffect(() => {
        if (isInitialMount.current) {
            previousFiscalYear.current = fiscalYear;
            previousImportId.current = importId;
            return;
        }

        const fiscalYearChanged = previousFiscalYear.current !== fiscalYear;
        const importIdChanged = previousImportId.current !== importId;

        if ((fiscalYearChanged || importIdChanged) && reload) {
            previousFiscalYear.current = fiscalYear;
            previousImportId.current = importId;

            reload();
        }
    }, [fiscalYear, importId, reload]);

    const {
        menuOrganizationProject,
        organizationProjects,
        projectFiscalYears,
        project,
        fiscalYears,
        fiscalYearsWithImportIds,
        importIdsFromUrlFiscalYear,
        fiscalYear: finalFiscalYear,
        importId: finalImportId,
        queryStatus: organizationProjectQueryStatus,
        fileTypeModulesByProject,
        hasSource,
        hasLegal
    } = organizationAndProjectSelection({
        fetchResults,
        fetchStatus,
        component: StructuredData,
        matchParams: effectiveMatchParams,
        props,
        i18n,
        locale,
        fiscalYear,
        importId,
        projectId,
        viewMode,
        route
    });

    const { loading = true, success = false } = organizationProjectQueryStatus || {};
    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);
    const projectModules = Array.isArray(project?.modules)
        ? project.modules.map(({ name }) => name).filter(Boolean)
        : [];
    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes);
    }, [organizationProjects, projectId, updateThemes]);

    fiscalYear = finalFiscalYear ?? fiscalYear;
    importId = finalImportId ?? importId;

    const { navData, importIdToUse, routeQueries, params, firstModuleFromProject } =
        buildGenericMenu(
            modules,
            fetchResults,
            locale,
            fiscalYear,
            projectId,
            importId,
            project,
            location,
            effectiveMatchParams,
            route,
            viewMode,
            fileTypeModulesByProject,
            fetchStatus
        );

    const genericOverviewRouteQuery = routeQueries.find((q) => q.resource === "generic_overview");
    const genericOverviewQueryName = genericOverviewRouteQuery
        ? alias({ ...genericOverviewRouteQuery, locale })
        : "";

    const {
        repositoryStatus = { status: 1 },
        getTotalExportLines = "",
        genericOverview,
    } = getQueryResultsArray(fetchResults, [
        {
            resultName: "repositoryStatus",
            queryName: `${locale}_repository_status_fiscalYear_${fiscalYear}_projectId_${projectId}${viewMode ? `_viewMode_${viewMode}` : ""}`,
        },
        {
            resultName: "getTotalExportLines",
            queryName: `${locale}_get_limit_export`,
        },
        {
            resultName: "genericOverview",
            queryName: genericOverviewQueryName,
        },
    ]);

    const getLayout = JSON.parse(genericOverview || "[]");

    changeDeepObject(getLayout, ["component", "cards"], {
        SubmitSalesInfo: { projectData: projectFiscalYears },
    });

    const headerComponents = buildHeaderComponents(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
    );
    const displayComponents = buildDisplayComponents(route, getLayout);

    const children = generateChildren({
        displayComponents,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        modules,
        fetch,
        pushModal,
        reload,
        onClickConfigurator: onClickConfiguratorApp,
    });

    const title = headerTitle(
        route,
        fetchResults,
        fetchStatus,
        routeQueries,
        props,
        params,
        i18n,
        locale,
        fiscalYear,
        importId,
    );

    const downloadableQueries = getDownloadableQueries(matchRoutes, route, location, params);

    const { navigation } = generateNavigation({
        modules,
        asideRepositoryNavigationByModule,
        route,
        fetchResults,
        fetchStatus,
        component: StructuredData,
        params,
        fiscalYear,
        importId,
        props,
        navData,
        i18n,
        locale,
    });

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    const appRedirect = appRedirectValidations({
        redirect,
        projectId,
        fiscalYear,
        importId,
        fiscalYears,
        appContext,
        match,
        matchParams,
        importIdToUse,
        firstModuleFromProject,
        section,
        fileType,
    });

    if (appRedirect) {
        return appRedirect;
    }

    const { navProps, staticLinks, leftMenuStaticLinks, hasNewNavigation } = buildNavProps(
        buildStaticLinks,
        navigation,
        viewModeHistory,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        viewMode,
    );

    const newMenuTreeList = (staticLinks || []).filter((menuItem) => {
        if (!menuItem?.useInMenuTreeList || !hasNewNavigation) return false;
        const searchParams = new URLSearchParams(location.search);
        const urlViewMode = searchParams.get("viewMode") || "source";
        return menuItem.viewMode === urlViewMode;
    });

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: {
            ...params,
            fiscalYear,
            importId,
            ...(viewMode && { viewMode }),
        },
        repositoryStatus,
        getTotalExportLines,
        staticLinks,
        styles,
        organizationProjects,
        leftMenuStaticLinks: hasNewNavigation ? leftMenuStaticLinks : [],
    };

    return (
        LoadingComponent || (
            <GlobalPageLayout
                menuEntries={newMenuTreeList}
                i18n={i18n}
                styles={styles}
                history={viewModeHistory}
                globalLayoutProps={globalLayoutProps}
                modules={modules}
                headerProps={{
                    genericSearchAction: true,
                    reportsAction: true,
                    appNewLogic: true,
                    fiscalYears: fiscalYearsWithImportIds,
                    importIds: appContext === "repository" ? "" : importIdsFromUrlFiscalYear,
                    isRepository: appContext === "repository",
                    externalViewToggle: appContext === "repository",
                    downloadableQueries,
                    hasSource,
                    hasLegal,
                    title:
                        (title && Array.isArray(title) ? title.join(" ") : title) ||
                        i18n`${params.section || ""}`,
                    totalLinesCsv: getTotalExportLines,
                    headerComponents: headerComponents,
                    onViewModeChange: viewMode ? setViewMode : null,
                    viewMode: viewMode ? viewMode : null,
                    titleBarActions:
                        headerComponents.filter((c) => !c.inline).length > 0 && !route?.disableHeaderTitleBarActions
                            ? headerComponents.filter((c) => !c.inline).map((headerComponent, index) => (
                                <headerComponent.Component
                                    key={`titlebar-action-${index}`}
                                    download={downloadAction}
                                    downloadableQueries={downloadableQueries}
                                    locale={locale}
                                    user={userInformation}
                                    items={route?.headerComponentConfigs?.items || []}
                                    label={i18n`${route?.headerComponentConfigs?.label || ""}`}
                                    {...headerComponent.props}
                                />
                            ))
                            : undefined,
                }}
                headerComponentsMapper={(downloading) => (headerComponentProperties) => (
                    <headerComponentProperties.Component
                        download={downloadAction}
                        downloading={downloading}
                        downloadableQueries={downloadableQueries}
                        locale={locale}
                        projectModules={projectModules}
                        user={userInformation}
                        items={route?.headerComponentConfigs?.items || []}
                        label={i18n`${route?.headerComponentConfigs?.label || ""}`}
                        {...headerComponentProperties.props}
                    />
                )}
                fetch={fetch}
                route={route}
                matchParams={matchParams}
                isLoadingContent={(fetch) => isPageLoading(fetch?.status, fetch?.activity)}
            >
                {children}
            </GlobalPageLayout>
        )
    );
};

StructuredData.queries = (args, userModulesByProject) => {
    return controllerQueries(args, userModulesByProject);
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        getFindingDetails,
        setDataFileStatus: setDataFileStatusAction,
        deleteFilePermanently: deleteFilePermanentlyAction,
        deleteInvalidFilePermanently: deleteInvalidFilePermanentlyAction,
        hideFile: hideFileAction,
        downloadFile: downloadFileAction,
        reprocessRepository: reprocessRepositoryAction,
        uploadFiles: uploadAction,
        download: downloadAction,
        submitAuditNote: auditNoteSubmit,
        genericSearchAction,
        reprocessFiles: reprocessFileAction,
        getDownloadTemplateData,
    },
)(withRouter(StructuredData));
